// A sample player page, reading the real Info Manager.
//
// A sample of how all the information about a player could be shown to
// someone deciding about him: a sample page, not production quality.
//
// So this is a LAB page, and it is deliberately outside the app's navigation:
// it exists to show that data reaches a screen, not to be a screen anyone
// navigates to. What it is careful about is the three things that would make it
// misleading rather than merely rough:
//
//   NOTHING INVENTED. Every value comes from a fact in the store. Where a value
//   is missing — and most weeks most of them are — it says which, and why.
//
//   NOTHING ASSERTED. "Tools, not instructions": no sentence here tells anybody
//   who to play. Numbers, labels, and what we do not know.
//
//   EVERYTHING TRACEABLE. The last panel names every source that contributed
//   and what could not be answered, which is the whole argument for the layer
//   underneath this page.

import { useParams, useSearchParams } from 'react-router-dom';
import { Cap, EmptyState, PositionPill, ProTeamCode, Surface } from '../kit';
import { Panel } from '../kit/Panel';
import { DashboardScreen, type Widget } from '../templates/DashboardScreen';
import { factIn, ownFact, useDossier } from '../data/dossier';
import { footballDirectoryMapping } from '@fsp/sports-football';
import type { Dossier } from '@fsp/info';
import '../styles/screens/lab.css';

// ── Reading the dossier ─────────────────────────────────────────────────────
//
// The store has already validated every payload against the schema registered
// for its kind, so this file narrows rather than parses. WHICH fact to narrow
// is the part that is easy to get wrong, and `data/dossier.ts` holds the two
// helpers that get it right: `ownFact` checks the subject ref, `factIn` names
// the section. Neither flattens the dossier looking for a kind — doing that
// returned a teammate's injury for anyone without one of his own.

interface Profile {
  displayName: string;
  position: string | null;
  birthDate: string | null;
  heightInches: number | null;
  weightPounds: number | null;
  college: string | null;
  draftYear: number | null;
  draftRound: number | null;
  draftPick: number | null;
  draftTeam: string | null;
}

interface Membership {
  team: string | null;
  jerseyNumber: number | null;
  yearsExperience: number | null;
  depthPosition: string | null;
  status: string | null;
  statusDetail: string | null;
  period: number | null;
}

/**
 * Whether this player can score at all this week.
 *
 * "Flag a slot that **cannot score** (empty, bye, Out)" is the one thing
 * CLAUDE.md asks a screen to say out loud, and it is not advice — a player on
 * injured reserve and a player nobody has looked at are the same blank
 * otherwise. The mapping itself belongs to the sport package, which is the only
 * place that knows what `RES` means.
 */
function availability(status: string | null): { label: string; canScore: boolean } {
  const mapped = footballDirectoryMapping.status({
    rosterStatus: status,
    profileStatus: null,
    onCurrentRoster: true,
  });
  switch (mapped) {
    case 'active':
      return { label: 'Active', canScore: true };
    case 'practice-squad':
      return { label: 'Practice squad', canScore: false };
    case 'retired':
      return { label: 'Retired', canScore: false };
    default:
      return { label: 'Not on the active roster', canScore: false };
  }
}

interface Injury {
  reportStatus: string | null;
  reportInjury: string | null;
  practiceStatus: string | null;
  practiceInjury: string | null;
}

interface Contest {
  gameId: string;
  home: string;
  away: string;
  kickoff: string | null;
  localTime: string | null;
  weekday: string | null;
  sourceZone?: string;
  venueName: string | null;
  roof: string | null;
  surface: string | null;
  indoors: boolean | null;
  location: string | null;
  homeRest: number | null;
  awayRest: number | null;
  spreadLine: number | null;
  totalLine: number | null;
}

interface Forecast {
  temperatureF: number | null;
  windMph: number | null;
  windGustMph: number | null;
  windDirection: string | null;
  precipitationChance: number | null;
  relativeHumidity: number | null;
  shortForecast: string | null;
  issuedAt: string;
  windowStart: string | null;
}

interface Gridpoint {
  timeZone: string | null;
}

interface VenueLocation {
  name: string;
  city: string;
  region: string;
  nwsCovered: boolean;
}

/** Years, from a birth date. Null rather than a guess when the date is absent. */
function ageFrom(birthDate: string | null, at: string): number | null {
  if (birthDate === null) return null;
  const born = Date.parse(birthDate);
  const now = Date.parse(at);
  if (Number.isNaN(born) || Number.isNaN(now)) return null;
  return Math.floor((now - born) / (365.2425 * 24 * 60 * 60 * 1000));
}

const inchesToFeet = (n: number | null): string | null =>
  n === null ? null : `${Math.floor(n / 12)}′ ${n % 12}″`;

/** The instant, where the game is played. Falls back to naming the league clock. */
function kickoffLabel(contest: Contest, zone: string | null): { primary: string; note: string } {
  if (contest.kickoff === null) {
    return { primary: 'Kickoff not set', note: 'the schedule carries no clock time for this game' };
  }
  const when = new Date(contest.kickoff);
  if (zone !== null) {
    const local = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
    }).format(when);
    return { primary: local, note: `local time at the venue · ${zone}` };
  }
  const et = new Intl.DateTimeFormat('en-US', {
    timeZone: contest.sourceZone ?? 'America/New_York',
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
  }).format(when);
  return { primary: et, note: 'Eastern — no venue time zone on record' };
}

// ── Pieces ──────────────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="fsp-lab-row">
      <Cap>{label}</Cap>
      <span className="fsp-num fsp-right">{value}</span>
    </div>
  );
}

/** A value we do not hold. Said out loud, with why, never rendered as a dash. */
function NotKnown({ children }: { children: React.ReactNode }) {
  return <span className="fsp-lab-not-known">{children}</span>;
}

function InjuryPanel({ injury }: { injury: Injury | null }) {
  if (injury === null) {
    return (
      <Panel title="Injury report">
        <EmptyState>
          Not on this week&rsquo;s report. Ninety-four percent of rostered players are not.
        </EmptyState>
      </Panel>
    );
  }

  // Both halves, never merged. The designation is the game-day call; the
  // practice column is what they actually did Wednesday to Friday, and the two
  // answer different questions — see B-53.
  return (
    <Panel title="Injury report" meta={<Cap>two signals</Cap>}>
      <Row
        label="Game-day designation"
        value={injury.reportStatus ?? <NotKnown>none given yet</NotKnown>}
      />
      <Row
        label="Listed with"
        value={injury.reportInjury ?? injury.practiceInjury ?? <NotKnown>not stated</NotKnown>}
      />
      <Row label="Practice" value={injury.practiceStatus ?? <NotKnown>not reported</NotKnown>} />
      {injury.reportStatus === null && injury.practiceStatus !== null ? (
        <p className="fsp-lab-note">
          On the report with no game-day designation. The player directory shows nothing for this
          week; the practice column is the only signal there is.
        </p>
      ) : null}
    </Panel>
  );
}

function WeatherPanel({
  forecast,
  contest,
}: {
  forecast: Forecast | null;
  contest: Contest | null;
}) {
  if (contest?.indoors === true) {
    return (
      <Panel title="Conditions">
        <Row label="Roof" value={contest.roof ?? 'closed'} />
        <p className="fsp-lab-note">Played indoors. Weather is not a factor, so none is fetched.</p>
      </Panel>
    );
  }

  if (forecast === null) {
    return (
      <Panel title="Conditions">
        <EmptyState>
          No forecast. The National Weather Service covers the United States and its territories
          only, and its hourly forecast reaches about six and a half days ahead.
        </EmptyState>
      </Panel>
    );
  }

  const wind =
    forecast.windMph === null
      ? null
      : forecast.windGustMph === null
        ? `${forecast.windMph} mph`
        : `${forecast.windMph}–${forecast.windGustMph} mph`;

  return (
    <Panel title="Conditions" meta={<Cap>at kickoff</Cap>}>
      <Row label="Sky" value={forecast.shortForecast ?? <NotKnown>not stated</NotKnown>} />
      <Row
        label="Temperature"
        value={
          forecast.temperatureF === null ? <NotKnown>—</NotKnown> : `${forecast.temperatureF}°F`
        }
      />
      <Row
        label="Wind"
        value={
          wind === null ? (
            <NotKnown>not stated</NotKnown>
          ) : (
            `${wind}${forecast.windDirection === null ? '' : ` ${forecast.windDirection}`}`
          )
        }
      />
      <Row
        label="Chance of precipitation"
        value={
          forecast.precipitationChance === null ? (
            <NotKnown>—</NotKnown>
          ) : (
            `${forecast.precipitationChance}%`
          )
        }
      />
      <p className="fsp-lab-note small">
        Issued {new Date(forecast.issuedAt).toUTCString()}. A forecast is what we believed then, not
        what happened.
      </p>
    </Panel>
  );
}

function TeammatesPanel({ dossier }: { dossier: Dossier }) {
  const section = dossier.sections.teammates;
  if (section === undefined) {
    const why = dossier.incomplete.find((i) => i.section === 'teammates');
    return (
      <Panel title="Around him">
        <EmptyState>{why?.detail ?? 'not assembled for this view'}</EmptyState>
      </Panel>
    );
  }

  // Pair each report to the person it is about, by OUR entity id rather than by
  // a vendor key out of the payload — the section carries both halves so a
  // screen never has to know that `personKey` means `nflverse-player`.
  const nameOf = new Map(section.entities.map((e) => [e.id, e.display]));
  const reports = section.facts
    .filter((f) => f.kind === 'injury.report')
    .map((f) => {
      const subject = f.refs.find((r) => r.role === 'subject')?.entityId;
      return {
        ...(f.payload as Injury & { position: string | null }),
        id: f.id,
        name: subject === undefined ? null : (nameOf.get(subject) ?? null),
      };
    });

  if (reports.length === 0) {
    return (
      <Panel title="Around him">
        <EmptyState>Nobody else on the roster is on this week&rsquo;s report.</EmptyState>
      </Panel>
    );
  }

  // Ordered by how much practice they missed, because that is the ordering the
  // data supports. No judgement about who matters — that is the reader's.
  const weight = (r: Injury) =>
    r.practiceStatus?.startsWith('Did Not') === true
      ? 0
      : r.practiceStatus?.startsWith('Limited') === true
        ? 1
        : 2;

  return (
    <Panel title="Around him" meta={<Cap>{reports.length} on the report</Cap>}>
      {[...reports]
        .sort((a, b) => weight(a) - weight(b))
        .map((r, i) => (
          <div key={r.id} className={`fsp-lab-mate${i === 0 ? ' first' : ''}`}>
            <span className="fsp-lab-mate-who">
              {r.position === null ? null : <PositionPill pos={r.position} size="sm" />}
              <span className="fsp-grow">
                <span className="fsp-lab-block">
                  {r.name ?? <NotKnown>name not resolved</NotKnown>}
                </span>
                <span className="fsp-lab-sub">
                  {r.practiceInjury ?? r.reportInjury ?? 'unspecified'}
                </span>
              </span>
            </span>
            <span className={`fsp-num fsp-right${weight(r) === 0 ? '' : ' fsp-lab-secondary'}`}>
              {r.practiceStatus ?? r.reportStatus ?? 'no detail'}
            </span>
          </div>
        ))}
    </Panel>
  );
}

function ProvenancePanel({ dossier }: { dossier: Dossier }) {
  return (
    <Panel title="Where this came from" meta={<Cap>{dossier.attribution.length} source(s)</Cap>}>
      {dossier.attribution.map((a) => (
        <div key={a.sourceId} className="fsp-lab-source-item">
          <div className="fsp-lab-strong">{a.name}</div>
          <div className="fsp-lab-sub">{a.license}</div>
          {a.text === null ? null : <div className="fsp-lab-sub">{a.text}</div>}
        </div>
      ))}

      {dossier.incomplete.length === 0 ? null : (
        <>
          <div className="fsp-lab-bench">
            <Cap>Not answered</Cap>
          </div>
          {dossier.incomplete.map((i) => (
            <div key={i.section} className="fsp-lab-gap">
              <span className="fsp-lab-strong">{i.section}</span>{' '}
              <span className="fsp-lab-secondary">— {i.detail}</span>
            </div>
          ))}
        </>
      )}

      <p className="fsp-lab-note small">As of {dossier.asOf}.</p>
    </Panel>
  );
}

// ── The screen ──────────────────────────────────────────────────────────────

export function LabPlayerScreen() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();

  const season = Number(search.get('season') ?? 2026);
  const period = Number(search.get('period') ?? 1);
  const namespace = search.get('ns');

  const { snapshot } = useDossier(
    id === undefined
      ? null
      : {
          id,
          season,
          period,
          ...(namespace === null ? {} : { namespace }),
        },
  );

  if (snapshot.state === 'loading') {
    return (
      <div className="fsp-screen fsp-lab-padded">
        <Surface>
          <Cap>Loading</Cap>
        </Surface>
      </div>
    );
  }

  const value = snapshot.state === 'ready' ? snapshot.value : snapshot.lastValue;
  if (value === null) {
    // Fail loudly. The reason the server gave, verbatim, rather than an empty
    // page that reads as "this player has nothing".
    return (
      <div className="fsp-screen fsp-lab-padded">
        <Surface>
          <h1 className="fsp-disp fsp-lab-title">Unavailable</h1>
          <p className="fsp-lab-secondary">
            {snapshot.state === 'failed' ? snapshot.failure.message : 'No dossier was asked for.'}
          </p>
          <Cap>{snapshot.state === 'failed' ? snapshot.failure.code : 'none'}</Cap>
        </Surface>
      </div>
    );
  }

  const d = value.dossier;
  // His own claims: the subject of each of these has to be him.
  const profile = ownFact(d, 'person.profile')?.payload as Profile | undefined;
  const membership = ownFact(d, 'roster.membership')?.payload as Membership | undefined;
  const injury = (ownFact(d, 'injury.report')?.payload as Injury | undefined) ?? null;

  // His week: facts about the fixture and the stadium, so he is never their
  // subject and the section is what ties them to him.
  const contest =
    (factIn(d, 'contest', 'contest.scheduled')?.payload as Contest | undefined) ?? null;
  const forecast =
    (factIn(d, 'environment', 'forecast.kickoff')?.payload as Forecast | undefined) ?? null;
  const grid = factIn(d, 'environment', 'venue.gridpoint')?.payload as Gridpoint | undefined;
  // Our curated name, not the fixture's: nflverse carries the name a venue had
  // in the season of each row, and 21 of 38 are a sponsor or two out of date.
  const venue = factIn(d, 'environment', 'venue.location')?.payload as VenueLocation | undefined;

  const team = membership?.team ?? null;
  const opponent =
    contest === null || team === null ? null : contest.home === team ? contest.away : contest.home;
  const atHome = contest !== null && team !== null && contest.home === team;
  const rest = contest === null ? null : atHome ? contest.homeRest : contest.awayRest;
  const when = contest === null ? null : kickoffLabel(contest, grid?.timeZone ?? null);

  const avail = availability(membership?.status ?? null);
  const bye = d.incomplete.find((i) => i.section === 'contest' && i.detail.includes('on a bye'));

  const widgets: Widget[] = [
    // Cannot-score comes first and cannot be missed. Not advice — the opposite:
    // without it, a player on injured reserve and a player nobody has looked at
    // are the same blank.
    ...(avail.canScore && bye === undefined
      ? []
      : [
          {
            id: 'cannot-score',
            pinned: true,
            content: (
              <Panel title="Cannot score this week">
                {bye === undefined ? null : <Row label="Fixture" value="Bye — no game" />}
                {avail.canScore ? null : <Row label="Roster" value={avail.label} />}
                <p className="fsp-lab-note">
                  {bye === undefined
                    ? 'Not on the active roster for this week.'
                    : 'The team has no fixture in this period.'}
                </p>
              </Panel>
            ),
          } as Widget,
        ]),
    {
      id: 'identity',
      pinned: true,
      content: (
        <Panel
          title={d.entity.display}
          meta={
            <span className="fsp-lab-meta">
              {profile?.position === null || profile?.position === undefined ? null : (
                <PositionPill pos={profile.position} />
              )}
              <ProTeamCode code={team} />
            </span>
          }
        >
          <Row
            label="Number"
            value={
              membership?.jerseyNumber === null || membership?.jerseyNumber === undefined ? (
                <NotKnown>not on the roster row</NotKnown>
              ) : (
                `#${membership.jerseyNumber}`
              )
            }
          />
          <Row
            label="Experience"
            value={
              membership?.yearsExperience === null || membership?.yearsExperience === undefined ? (
                <NotKnown>not stated</NotKnown>
              ) : (
                `${membership.yearsExperience} seasons`
              )
            }
          />
          <Row
            label="Age"
            value={
              ageFrom(profile?.birthDate ?? null, d.asOf) ?? <NotKnown>no birth date</NotKnown>
            }
          />
          <Row
            label="Height and weight"
            value={
              profile === undefined ||
              profile.heightInches === null ||
              profile.weightPounds === null ? (
                <NotKnown>not stated</NotKnown>
              ) : (
                `${inchesToFeet(profile.heightInches)}, ${profile.weightPounds} lb`
              )
            }
          />
          <Row
            label="Roster status"
            value={
              <span className={avail.canScore ? undefined : 'fsp-lab-secondary'}>
                {avail.label}
                {membership?.statusDetail === null || membership?.statusDetail === undefined
                  ? ''
                  : ` · ${membership.statusDetail}`}
              </span>
            }
          />
          {membership?.period === null ||
          membership?.period === undefined ||
          membership.period === period ? null : (
            <Row
              label="Roster as of"
              value={<NotKnown>{`period ${membership.period} — the latest published`}</NotKnown>}
            />
          )}
          <Row label="College" value={profile?.college ?? <NotKnown>not stated</NotKnown>} />
          <Row
            label="Drafted"
            value={
              profile?.draftYear === null || profile?.draftYear === undefined ? (
                <NotKnown>undrafted</NotKnown>
              ) : (
                `${profile.draftYear} · round ${profile.draftRound ?? '?'}, pick ${profile.draftPick ?? '?'}${profile.draftTeam === null ? '' : ` · ${profile.draftTeam}`}`
              )
            }
          />
        </Panel>
      ),
    },

    { id: 'injury', column: 1, content: <InjuryPanel injury={injury} /> },

    {
      id: 'fixture',
      column: 2,
      content:
        contest === null ? (
          <Panel title="This week">
            <EmptyState>
              {d.incomplete.find((i) => i.section === 'contest')?.detail ??
                'no fixture for this period'}
            </EmptyState>
          </Panel>
        ) : (
          <Panel title="This week" meta={<Cap>{`${season} · period ${period}`}</Cap>}>
            <Row
              label={atHome ? 'Hosting' : 'Away at'}
              value={opponent ?? <NotKnown>unknown</NotKnown>}
            />
            <Row label="Kickoff" value={when?.primary ?? <NotKnown>not set</NotKnown>} />
            <Row
              label="Venue"
              value={venue?.name ?? contest.venueName ?? <NotKnown>not stated</NotKnown>}
            />
            {venue === undefined ? null : (
              <Row label="City" value={`${venue.city}, ${venue.region}`} />
            )}
            <Row
              label="Surface and roof"
              value={`${contest.surface ?? 'unknown surface'} · ${contest.roof ?? 'roof unknown'}`}
            />
            <Row
              label="Days of rest"
              value={rest === null ? <NotKnown>not stated</NotKnown> : rest}
            />
            {contest.location === 'Neutral' ? (
              <Row label="Site" value="Neutral — not the home team's own venue" />
            ) : null}
            <p className="fsp-lab-note small">{when?.note}</p>
          </Panel>
        ),
    },

    { id: 'weather', column: 3, content: <WeatherPanel forecast={forecast} contest={contest} /> },

    { id: 'teammates', column: 1, content: <TeammatesPanel dossier={d} /> },

    {
      id: 'market',
      column: 2,
      content:
        contest === null ? (
          <Panel title="Market">
            <EmptyState>No fixture, so no line.</EmptyState>
          </Panel>
        ) : (
          <Panel title="Market" meta={<Cap>as published</Cap>}>
            <Row
              label="Spread"
              value={
                contest.spreadLine === null ? <NotKnown>no line yet</NotKnown> : contest.spreadLine
              }
            />
            <Row
              label="Total"
              value={
                contest.totalLine === null ? <NotKnown>no line yet</NotKnown> : contest.totalLine
              }
            />
            <p className="fsp-lab-note small">
              Carried in the schedule file. Weeks 10 to 18 have none yet.
            </p>
          </Panel>
        ),
    },

    { id: 'provenance', column: 3, content: <ProvenancePanel dossier={d} /> },
  ];

  return <DashboardScreen widgets={widgets} />;
}
